// EmbeddedDesignPage.jsx
// Theme: Matches bibaelectronics.com — sage-cream bg, charcoal text,
// green accent, white cards, editorial DM Serif Display typography.
// Requires: framer-motion  →  npm install framer-motion

import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import "./EmbeddedDesignPage.css";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

/* =====================
   ANIMATION VARIANTS
===================== */
const fadeUp = {
  hidden:  { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const fadeLeft = {
  hidden:  { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0,  transition: { duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const fadeRight = {
  hidden:  { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0,  transition: { duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const staggerList = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
};

const listItem = {
  hidden:  { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.45, ease: "easeOut" } },
};

const VIEW = { once: true, amount: 0.25 };

/* =====================
   ICONS (inline SVG — no external deps)
===================== */
const CheckIcon = () => (
  <svg viewBox="0 0 12 12" width="10" height="10">
    <polyline points="2,6 5,9 10,3" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/* Why-card icons */
const icons = {
  cpu:      <circle cx="12" cy="12" r="10"/>,
  layers:   <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2,17 12,22 22,17"/><polyline points="2,12 12,17 22,12"/></>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>,
  shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  zap:      <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
  clock:    <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
};

const WhyIcon = ({ name }) => (
  <div className="why-card-icon">
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      {icons[name] || icons.zap}
    </svg>
  </div>
);

/* =====================
   STATIC DATA
===================== */
const SERVICES = [
  {
    id: 1,
    slug: "firmware",
    label: "Core Capability",
    title: "Firmware",
    titleAccent: "Development",
    description:
      "We develop robust firmware for embedded devices, ensuring reliability, performance, and seamless integration across platforms and applications. Our team works with bare-metal to RTOS-based systems.",
    services: [
      "System Architecture Design",
      "RTOS / OS / Bare Metal Apps",
      "Electronic Design & Development",
      "BSP & Device Drivers",
      "Porting and Optimization",
    ],
    img: "/WebPictures/FirmwareDevlopment.webp",
    badge: "Firmware & OS",
    colorClass: "accent-green",
  },
  {
    id: 2,
    slug: "hardware",
    label: "Electronics",
    title: "Hardware &",
    titleAccent: "Electrical Design",
    description:
      "Hardware and electrical design form the foundation of every great product. From concept to production-ready schematics, BIBA delivers precision-engineered circuits covering every stage of development.",
    services: [
      "High-Speed Digital Circuit Design",
      "Analog Circuit Design",
      "Power Electronics",
      "Low-Power Circuit Design",
      "Signal Integrity Analysis",
    ],
    img: "/hardware.jpg",
    badge: "PCB & Schematics",
    colorClass: "accent-blue",
  },
  {
    id: 3,
    slug: "pcb",
    label: "Fabrication",
    title: "PCB Layout",
    titleAccent: "Design",
    description:
      "Almost all electronic products contain PCBs. BIBA provides end-to-end PCB layout services — from multilayer stack-ups to DFM analysis — ensuring manufacturability and signal integrity from the start.",
    services: [
      "Multilayer PCB Layout Design",
      "DFM / DFT / DFA Analysis",
      "Thermal Analysis",
    ],
    img: "/WebPictures/PCBLayout.jpg",
    badge: "Manufacturing-Ready",
    colorClass: "accent-orange",
  },
  {
    id: 4,
    slug: "software",
    label: "Application Layer",
    title: "Software",
    titleAccent: "Design",
    description:
      "Software is what brings hardware to life. Our software engineering team delivers everything from system architecture to deployment — built for performance, reliability, and long-term maintainability.",
    services: [
      "Planning & Requirements Analysis",
      "System Architecture Design",
      "Development & Coding",
      "Testing & Quality Assurance",
      "Deployment & Maintenance",
    ],
    img: "/WebPictures/SoftwareDesign.jpg",
    badge: "Full-Stack Embedded",
    colorClass: "accent-purple",
  },
  {
    id: 5,
    slug: "mechanical",
    label: "Enclosure & Form",
    title: "Mechanical",
    titleAccent: "Design",
    description:
      "Great embedded products need great enclosures. Our mechanical engineering team delivers 2D/3D CAD, simulation, and prototype-to-mold services ensuring your product is as precise on the outside as within.",
    services: [
      "2D & 3D CAD Design",
      "Mechanical Simulation",
      "Prototype Development",
      "Mold Design",
    ],
    img: "/WebPictures/MechanicalDesign.jpg",
    badge: "CAD & Mold",
    colorClass: "accent-red",
  },
];

const WHY_FEATURES = [
  { icon: "cpu",      title: "End-to-End Ownership",        desc: "From concept to production — firmware, hardware, software, and mechanical design under one roof.",         featured: false },
  { icon: "shield",   title: "Quality & Compliance",        desc: "Every design is validated against industry standards. RoHS compliant, DFM-reviewed, and production-tested.", featured: true  },
  { icon: "zap",      title: "Faster Time-to-Market",       desc: "Streamlined workflows and in-house expertise cut iteration cycles and get your product to market sooner.",   featured: false },
  { icon: "layers",   title: "Multi-Domain Expertise",      desc: "Our team spans firmware, hardware, software, and mechanical — so transitions between phases are seamless.",  featured: false },
  { icon: "settings", title: "Custom-Tailored Solutions",   desc: "No templates. Every embedded system is engineered from first principles to match your exact requirements.",  featured: false },
  { icon: "clock",    title: "Long-Term Partnership",       desc: "We support you beyond deployment — maintenance, iteration, and scaling as your product evolves.",            featured: false },
];

/* =====================
   IMAGE WRAPPER
   Uses real img if available, placeholder otherwise
===================== */
const ServiceImage = ({ src, alt, badge }) => (
  <div className="service-image-wrap">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
    />
    <div className="service-img-placeholder" style={{ display: "none" }}>
      <span className="ph-icon">🖥️</span>
      <span className="ph-text">{alt}</span>
      <span className="ph-path">{src}</span>
    </div>
    <div className="service-image-badge">
      <span className="badge-dot" />
      {badge}
    </div>
  </div>
);

/* =====================
   MAIN COMPONENT
===================== */
const DEFAULT_HERO_DURATION_MS = 5000;

const EmbeddedDesignPage = () => {
  const [heroBanners, setHeroBanners] = useState([{ src: "/bg.gif", durationMs: DEFAULT_HERO_DURATION_MS }]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [searchParams] = useSearchParams();
  const serviceRefs = useRef({});

  // Fetches the home_banner docs whose location === "Embedded Design" and
  // status === true, then swaps them in for the default local hero
  // background. Each banner carries its own on-screen duration
  // (durationSeconds in Firestore, set from Tixaru's Biba Website >
  // Banners page), defaulting to DEFAULT_HERO_DURATION_MS for banners
  // saved before that field existed.
  useEffect(() => {
    const loadBanner = async () => {
      try {
        const bannerQuery = query(
          collection(db, "home_banner"),
          where("location", "==", "Embedded Design"),
          where("status", "==", true)
        );
        const snapshot = await getDocs(bannerQuery);

        const banners = snapshot.docs
          .map((doc) => doc.data())
          .sort((a, b) => {
            const orderDiff = (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
            return orderDiff !== 0 ? orderDiff : (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
          })
          .map((banner) => ({
            src: banner.image,
            durationMs: (Number(banner.durationSeconds) || DEFAULT_HERO_DURATION_MS / 1000) * 1000,
          }))
          .filter((banner) => Boolean(banner.src));

        if (banners.length > 0) {
          setHeroBanners(banners);
          setHeroIndex(0);
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadBanner();
  }, []);

  // Auto-swap between hero banners when there's more than one for this
  // section, each shown for its own duration.
  useEffect(() => {
    if (heroBanners.length < 2) return undefined;
    const duration = heroBanners[heroIndex]?.durationMs ?? DEFAULT_HERO_DURATION_MS;
    const timer = setTimeout(() => {
      setHeroIndex((i) => (i + 1) % heroBanners.length);
    }, duration);
    return () => clearTimeout(timer);
  }, [heroBanners, heroIndex]);

  const heroImage = heroBanners[heroIndex]?.src ?? heroBanners[0].src;

  // Handle scroll-to-service on component mount or query param change
  useEffect(() => {
    const serviceSlug = searchParams.get("service");
    
    if (serviceSlug && serviceRefs.current[serviceSlug]) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        serviceRefs.current[serviceSlug].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [searchParams]);

  return (
    <div className="embedded-page">

      {/* ===========================
          HERO
      =========================== */}
      <div className="embedded-hero">
        <img
          src={heroImage}
          alt="Embedded Design Services background"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="embedded-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <span className="hero-label">Embedded Design Services</span>
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            Engineering the<br />
            <span>Intelligent</span> Core
          </motion.h1>

          <motion.p
            className="hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
          >
            End-to-end embedded solutions — hardware, firmware, software,
            and mechanical — built for precision, reliability, and scale.
          </motion.p>
        </div>
      </div>

      {/* Breadcrumbs - sit below the hero banner */}
      <div className="embedded-breadcrumb-bar">
        <div className="embedded-breadcrumbs">
          <a href="/">Home</a>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Embedded Design Services</span>
        </div>
      </div>

      {/* ===========================
          INTRO / DESCRIPTION
      =========================== */}
      <section className="embedded-intro">
        <div className="intro-inner">
          {/* Left — heading */}
          <motion.div
            className="intro-left"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={VIEW}
          >
            <span className="intro-tag">About Our Services</span>
            <h2>
              Where Design<br />Meets <span>Precision</span>
            </h2>

            <div className="intro-stats">
              {[
                { num: "5+",   label: "Design Domains"    },
                { num: "100+", label: "Projects Delivered" },
                { num: "2021", label: "Founded"            },
              ].map((s) => (
                <div key={s.label} className="stat-chip">
                  <span className="stat-num">{s.num}</span>
                  <span className="stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — paragraphs */}
          <motion.div
            className="intro-right"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={VIEW}
          >
            <p>
              At <strong>BIBA Electronics</strong>, we deliver end-to-end{" "}
              <em>Embedded Design Services</em> that empower businesses with
              intelligent, connected, and future-ready solutions. Our expertise
              spans <em>hardware design</em>, <em>firmware development</em>, and{" "}
              <em>software engineering</em> — ensuring seamless integration between
              devices and applications.
            </p>
            <p>
              With a strong focus on <strong>quality, scalability, and compliance</strong>,
              we design embedded systems that meet the highest industry standards — from
              concept and prototyping to{" "}
              <strong>system integration, certification, and deployment</strong>.
            </p>
            <p>
              Backed by seasoned engineers and innovation-driven specialists, BIBA provides
              custom-tailored embedded technologies that align with your business goals —
              crafting solutions that <strong>guarantee reliability, efficiency, and long-term success</strong>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===========================
          SERVICE SECTIONS (alternating)
      =========================== */}
      <div className="services-wrapper">
        {SERVICES.map((svc, idx) => {
          const isReverse = idx % 2 !== 0;
          const contentAnim  = isReverse ? fadeRight : fadeLeft;
          const imageAnim    = isReverse ? fadeLeft  : fadeRight;

          return (
            <div
              key={svc.id}
              id={`service-${svc.slug}`}
              ref={(el) => {
                if (el) serviceRefs.current[svc.slug] = el;
              }}
              className={`service-block${isReverse ? " reverse" : ""}`}
            >
              {/* Content */}
              <motion.div
                className="service-content"
                variants={contentAnim}
                initial="hidden"
                whileInView="visible"
                viewport={VIEW}
              >
                
                <span className="service-label">{svc.label}</span>
                <h2>
                  {svc.title}{" "}
                  <span>{svc.titleAccent}</span>
                </h2>
                <p>{svc.description}</p>

                <motion.ul
                  className="service-list"
                  variants={staggerList}
                  initial="hidden"
                  whileInView="visible"
                  viewport={VIEW}
                  style={{ listStyle: "none" }}
                >
                  {svc.services.map((item) => (
                    <motion.li key={item} variants={listItem} className="service-list-item">
                      <span className="check-icon"><CheckIcon /></span>
                      {item}
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>

              {/* Image */}
              <motion.div
                variants={imageAnim}
                initial="hidden"
                whileInView="visible"
                viewport={VIEW}
              >
                <ServiceImage
                  src={svc.img}
                  alt={`${svc.title} ${svc.titleAccent}`}
                  badge={svc.badge}
                />
              </motion.div>
            </div>
          );
        })}
      </div>

      {/* ===========================
          WHY CHOOSE BIBA — feature grid
      =========================== */}
      <section className="why-section">
        <div className="why-inner">
          <motion.div
            className="why-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VIEW}
          >
            <span className="section-tag">Why Choose BIBA</span>
            <h2>
              Built for Precision,<br />
              <span>Designed to Last</span>
            </h2>
            <p>
              Six reasons why industry-leading businesses trust BIBA Electronics
              for their most critical embedded systems.
            </p>
          </motion.div>

          <motion.div
            className="why-grid"
            variants={staggerList}
            initial="hidden"
            whileInView="visible"
            viewport={VIEW}
          >
            {WHY_FEATURES.map((f) => (
              <motion.div
                key={f.title}
                className={`why-card${f.featured ? " featured" : ""}`}
                variants={listItem}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <WhyIcon name={f.icon} />
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===========================
          CTA BAND
      =========================== */}
      <motion.div
        className="cta-band"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={VIEW}
        transition={{ duration: 0.7 }}
      >
        <div className="cta-text">
          <h2>Ready to build something remarkable?</h2>
          <p>Tell us about your project — our engineers are ready to help.</p>
        </div>
        <div className="cta-actions">
          <a href="/contact" className="cta-btn-primary">
            Get in Touch →
          </a>
          <a href="https://www.bibaelectronics.com/category/tft-and-touch/tft-display" className="cta-btn-outline">
            Explore Products
          </a>
        </div>
      </motion.div>

    </div>
  );
};

export default EmbeddedDesignPage;